import React from 'react';
import TruncatedText from 'components/TruncatedText';
import { Submission } from 'types/evaluation';
import { useRoleInfo } from 'hooks/useRoleInfo';

interface Aspect {
  name: string;
  weight: number;
}

interface Judgment {
  overall_summary?: string;
  aspect_scores?: Array<{ value: number }>;
  documentId?: string;
}

type SaveState = 'initial' | 'saved' | 'changed' | 'completed';

interface EvaluationTableProps {
  selectedCategory: string;
  filteredSubmissions: Submission[];
  aspects: Aspect[];
  loadingCriteria: boolean;
  scores: Record<string, Record<string, number | string>>;
  saveStates: Record<string, SaveState>;
  isSubmitting: Record<string, boolean>;
  existingJudgments: Record<string, Judgment>;
  isIndividualCategory: (category: string) => boolean;
  getEvaluationStatus: (submission: Submission) => {
    status: string;
    label: string;
    color: string;
  };
  handleScoreChange: (
    submissionId: string,
    criteriaKey: string,
    value: string
  ) => void;
  handleSubmissionSave: (submission: Submission) => void;
  isReadOnly?: boolean;
}

// 3rd Container: Evaluation Table
const EvaluationTable: React.FC<EvaluationTableProps> = ({
  selectedCategory,
  filteredSubmissions,
  aspects,
  loadingCriteria,
  scores,
  saveStates,
  isSubmitting,
  existingJudgments,
  isIndividualCategory,
  handleScoreChange,
  handleSubmissionSave,
  isReadOnly = false,
}) => {
  const { userIsSuperAdmin } = useRoleInfo();

  if (!selectedCategory || filteredSubmissions.length === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
      {filteredSubmissions.length === 0 ? (
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-baijamjuree font-bold text-gray-900 mb-2">
            No finalists found
          </h3>
          <p className="text-gray-600 font-baijamjuree">
            {selectedCategory
              ? `No finalists found in the "${selectedCategory}" category.`
              : 'No finalists match your current search criteria.'}
          </p>
        </div>
      ) : (
        <table className="w-full table-fixed">
          <thead className="bg-gray-900">
            <tr>
              <th
                className="text-center py-4 px-2 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '4%' }}
              >
                No.
              </th>
              <th
                className="text-left py-4 px-3 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '18%' }}
              >
                {isIndividualCategory(selectedCategory)
                  ? 'Profile Name'
                  : 'Project Name'}
              </th>
              <th
                className="text-left py-4 px-3 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '13%' }}
              >
                Pillar
              </th>
              <th
                className="text-left py-4 px-3 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '13%' }}
              >
                Submitter
              </th>
              {loadingCriteria ? (
                <th
                  className="text-center py-4 px-2 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                  style={{ width: '42%' }}
                >
                  Loading criteria...
                </th>
              ) : (
                aspects.map((aspect, index) => {
                  // Clean up aspect name by removing category prefix
                  const cleanName = aspect.name.replace(/^.*?\s*-\s*/, '');
                  return (
                    <th
                      key={index}
                      className="text-center py-4 px-2 font-baijamjuree font-semibold text-white border-r border-gray-600"
                      style={{ width: `${42 / aspects.length}%` }}
                    >
                      <div className="text-sm leading-tight">{cleanName}</div>
                    </th>
                  );
                })
              )}
              <th
                className="text-center py-4 px-2 font-baijamjuree font-semibold text-sm text-white"
                style={{ width: '12%' }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredSubmissions.map((submission, rowIndex) => {
              const submissionId = submission.documentId || submission.id || '';
              const displayName = isIndividualCategory(submission.category)
                ? submission.full_name
                : submission.project_name || submission.description;
              const submissionScores = scores[submissionId] || {};
              const isSubmittingThis = isSubmitting[submissionId];

              return (
                <tr
                  key={submissionId}
                  className="hover:bg-gray-50 transition-colors duration-200"
                >
                  <td className="py-4 px-2 align-middle border-r border-gray-100 text-center">
                    <span className="text-sm text-gray-500 font-baijamjuree font-medium">
                      {rowIndex + 1}
                    </span>
                  </td>
                  <td className="py-4 px-3 align-middle border-r border-gray-100">
                    <div>
                      <TruncatedText
                        text={displayName || ''}
                        className="font-medium text-gray-900 text-base"
                        maxLines={2}
                      />
                      {isIndividualCategory(submission.category) &&
                        submission.project_name && (
                          <TruncatedText
                            text={`Project: ${submission.project_name}`}
                            className="text-sm text-gray-500 mt-1"
                            maxLines={1}
                          />
                        )}
                    </div>
                  </td>

                  <td className="py-4 px-3 align-middle border-r border-gray-100">
                    <div className="text-base text-gray-600 font-baijamjuree font-medium">
                      {submission.pillar}
                    </div>
                  </td>
                  <td className="py-4 px-3 align-middle border-r border-gray-100">
                    <TruncatedText
                      text={submission.full_name || 'N/A'}
                      className="text-base text-gray-600 font-baijamjuree font-medium"
                      maxLines={2}
                    />
                  </td>
                  {loadingCriteria ? (
                    <td
                      className="py-4 px-4 align-middle text-center"
                      colSpan={aspects.length || 3}
                    >
                      <div className="text-gray-500 font-baijamjuree text-sm">
                        Loading criteria...
                      </div>
                    </td>
                  ) : (
                    aspects.map((aspect, index) => {
                      const criteriaKey = `criteria${index + 1}`;
                      const currentScore = submissionScores[criteriaKey];

                      return (
                        <td
                          key={index}
                          className="py-4 px-2 align-middle border-r border-gray-100"
                        >
                          <div className="flex justify-center">
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={currentScore}
                              onChange={e =>
                                handleScoreChange(
                                  submissionId,
                                  criteriaKey,
                                  e.target.value
                                )
                              }
                              className="w-20 px-3 py-2 border border-gray-300 rounded text-center font-baijamjuree text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="0-10"
                              disabled={
                                !userIsSuperAdmin &&
                                (isReadOnly ||
                                  existingJudgments[submissionId]
                                    ?.overall_summary === 'Completed Final')
                              }
                            />
                          </div>
                        </td>
                      );
                    })
                  )}
                  <td className="py-4 px-2 align-middle text-center">
                    {(() => {
                      // Check if all scores are filled for this submission
                      const allScoresFilled = aspects.every((_, index) => {
                        const score = submissionScores[`criteria${index + 1}`];
                        const numScore = Number(score);
                        return score !== '' && numScore >= 0 && numScore <= 10;
                      });

                      // Get current save state for this submission
                      const currentSaveState =
                        saveStates[submissionId] || 'initial';

                      // Determine button style and text based on save state
                      let buttonClass =
                        'px-4 py-2 rounded-full font-medium font-baijamjuree text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ';
                      let buttonText = 'Save';

                      if (isSubmittingThis) {
                        buttonClass += 'bg-gray-400 text-white';
                        buttonText = 'Saving...';
                      } else {
                        switch (currentSaveState) {
                          case 'initial':
                            // Initial state: Grey-ish Save (when no scores or not all filled)
                            if (allScoresFilled) {
                              buttonClass +=
                                'bg-black text-white hover:bg-gray-800 hover:shadow-lg transform hover:scale-105';
                              buttonText = 'Save';
                            } else {
                              buttonClass +=
                                'bg-gray-300 text-gray-600 hover:bg-gray-400 hover:text-white';
                              buttonText = 'Save';
                            }
                            break;
                          case 'saved':
                            // When saved: Green Saved
                            buttonClass +=
                              'bg-green-500 text-white hover:bg-green-600 hover:shadow-lg transform hover:scale-105';
                            buttonText = 'Saved';
                            break;
                          case 'changed':
                            // When changes occur: Yellow Update
                            buttonClass +=
                              'bg-yellow-500 text-white hover:bg-yellow-600 hover:shadow-lg transform hover:scale-105';
                            buttonText = 'Update';
                            break;
                          case 'completed':
                            // When evaluation is completed (not draft): Green Saved (read-only state)
                            buttonClass +=
                              'bg-green-500 text-white hover:bg-green-600 hover:shadow-lg transform hover:scale-105';
                            buttonText = 'Completed';
                            break;
                          default:
                            buttonClass +=
                              'bg-gray-300 text-gray-600 hover:bg-gray-400 hover:text-white';
                            buttonText = 'Save';
                        }
                      }

                      return (
                        <button
                          onClick={() => handleSubmissionSave(submission)}
                          disabled={
                            isSubmittingThis ||
                            (!userIsSuperAdmin &&
                              (isReadOnly ||
                                currentSaveState === 'completed' ||
                                existingJudgments[submissionId]
                                  ?.overall_summary === 'Completed Final'))
                          }
                          className={buttonClass}
                        >
                          {buttonText}
                        </button>
                      );
                    })()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default EvaluationTable;
